import { Capacitor } from '@capacitor/core';
import type { SecureStoragePlugin } from '@aparajita/capacitor-secure-storage';
import { IS_SERVER } from '@olo-web/utils/common/constants';

export interface IPersistedSession {
  customerId: string;
  sessionId: string;
  sessionSecret: string;
  loginTimestamp: string | number;
  firstName?: string;
  lastName?: string;
}

const STORAGE_KEY = 'so_customer_session';

// Session persistence is native-only. On web (and during SSR) every function is
// a no-op so the existing in-memory/sessionStorage login lifecycle is unchanged.
//
// The isPluginAvailable check additionally guards *older* native binaries that
// were shipped before the SecureStorage plugin was bundled: there the JS loads
// fine but the native module is absent, so bridge calls reject as UNIMPLEMENTED
// (or hang). Checking availability up front lets those apps degrade cleanly
// instead of relying on downstream try/catch and launch-gate timeouts.
const isSecureStorageAvailable = (): boolean =>
  !IS_SERVER && Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SecureStorage');

let configurePromise: Promise<unknown> | null = null;

// NOTE: the returned plugin is wrapped in a plain object. Capacitor plugin
// proxies expose a function for *every* property access (including `.then`),
// which makes them look like thenables. If an async function resolved directly
// to the proxy, the Promise machinery would call `SecureStorage.then()` while
// unwrapping it, which Capacitor rejects as UNIMPLEMENTED. Wrapping avoids that.
async function getStore(): Promise<{ store: SecureStoragePlugin }> {
  const { SecureStorage, KeychainAccess } = await import('@aparajita/capacitor-secure-storage');

  // Configure once: keep the credential on-device (no iCloud sync) and readable
  // after first unlock so we can rehydrate on cold launch.
  if (!configurePromise) {
    configurePromise = Promise.all([
      SecureStorage.setSynchronize(false),
      SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly),
    ]).catch((error) => {
      // Reset so a later call can retry configuration.
      configurePromise = null;
      // Log only the message: the raw error can be forwarded to RUM, and these
      // code paths handle the session (incl. sessionSecret) — avoid leaking it.
      console.error('Failed to configure secure storage', error?.message);
    });
  }
  await configurePromise;

  return { store: SecureStorage };
}

export async function saveSession(session: IPersistedSession): Promise<void> {
  if (!isSecureStorageAvailable()) return;
  try {
    const { store } = await getStore();
    await store.set(STORAGE_KEY, session as unknown as Record<string, unknown>);
  } catch (error) {
    // Log only the message, never the raw error: it can be forwarded to RUM and
    // this is the one call site whose input carries the live sessionSecret.
    console.error('Failed to persist customer session', error?.message);
  }
}

export async function loadSession(): Promise<IPersistedSession | null> {
  if (!isSecureStorageAvailable()) return null;
  try {
    const { store } = await getStore();
    const data = await store.get(STORAGE_KEY, false);
    if (!data || typeof data !== 'object') return null;

    const session = data as Partial<IPersistedSession>;
    if (!session.customerId || !session.sessionId || !session.sessionSecret) return null;

    return session as IPersistedSession;
  } catch (error) {
    console.error('Failed to load customer session', error?.message);
    return null;
  }
}

export async function clearSession(): Promise<void> {
  if (!isSecureStorageAvailable()) return;
  try {
    const { store } = await getStore();
    await store.remove(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear customer session', error?.message);
  }
}
